import * as React from "react";
import { cn } from "@/lib/utils";

export interface InputProps extends React.ComponentProps<"input"> {
  loading?: boolean;
  containerClassName?: string;
  /**
   * Validation error. Non-empty string toggles the error border, shakes the
   * input on transition false → true, and reveals the message below.
   */
  error?: string;
}

const readMs = (name: string, fallback: number) => {
  if (typeof window === "undefined") return fallback;
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
  return Number.isFinite(v) ? v : fallback;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, containerClassName, type, loading, disabled, error, ...props }, ref) => {
    const hasError = !!error;
    const [isShaking, setIsShaking] = React.useState(false);
    const prevHadError = React.useRef(false);

    React.useEffect(() => {
      const hadError = prevHadError.current;
      prevHadError.current = hasError;
      if (!hasError || hadError) return undefined;
      setIsShaking(false);
      const raf = requestAnimationFrame(() => setIsShaking(true));
      const total = readMs("--shake-dur-a", 80) * 2 + readMs("--shake-dur-b", 60) * 2;
      const clear = window.setTimeout(() => setIsShaking(false), total + 20);
      return () => {
        cancelAnimationFrame(raf);
        window.clearTimeout(clear);
      };
    }, [hasError]);

    return (
      <div className={cn("t-input-wrap relative", hasError && "is-error", containerClassName)}>
        <input
          type={type}
          ref={ref}
          disabled={disabled || loading}
          aria-invalid={hasError || undefined}
          className={cn(
            "t-input",
            "flex h-10 w-full rounded-large border border-cede-border-container bg-cede-bg-component px-3 py-2 body1 text-cede-text-primary placeholder:text-cede-text-secondary focus:outline-none focus:border-cede-primary focus-visible:ring-2 focus-visible:ring-cede-primary disabled:cursor-not-allowed disabled:opacity-50",
            "aria-invalid:border-cede-error aria-invalid:focus:border-cede-error",
            hasError && "is-error",
            isShaking && "is-shaking",
            className,
          )}
          {...props}
        />
        {loading && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-cede-primary border-t-transparent" />
          </div>
        )}
        {error && <p className="t-error-msg mt-1 text-xs text-cede-error">{error}</p>}
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input };
